import React, { ChangeEvent, useMemo } from 'react'
import { NumberInput } from '@structura/ui'
import { CrossSection, SMBeamModel } from '../../types'
import { LoadFormState, LoadState, LoadTypeOption } from '../../hooks/useLoadState'
import { useTranslation } from '@structura/i18n'

interface LoadInputFormProps
{
  model: SMBeamModel
  selectedLoadType: LoadTypeOption
  loadForm: LoadFormState
  setLoadForm: LoadState['setLoadForm']
  calculateSelfWeight: (crossSection: CrossSection) => number
  addLoadToSpan: () => void
  editingLoadId?: string | null
  onCancelEdit?: () => void
}

const LoadInputForm: React.FC<LoadInputFormProps> = ({
  model,
  selectedLoadType,
  loadForm,
  setLoadForm,
  calculateSelfWeight,
  addLoadToSpan,
  editingLoadId,
  onCancelEdit
}) =>
{
  const { t } = useTranslation()
  const autoSelfWeight = useMemo(() => 0 - Math.abs(calculateSelfWeight(model.crossSection)), [calculateSelfWeight, model.crossSection])

  const updateForm = (updates: Partial<LoadFormState>) =>
  {
    setLoadForm(previous => ({
      ...previous,
      ...updates
    }))
  }

  const handleAxialForceEndChange = (event: ChangeEvent<HTMLSelectElement>) =>
  {
    updateForm({ axialForceEnd: event.target.value as 'left' | 'right' })
  }

  return (
    <div className="mb-3">
      <div className="row">
        {(selectedLoadType === 'UniformLoad' || selectedLoadType === 'SelfWeight') && (
          <div className="col-12">
            <label className="form-label">
              {selectedLoadType === 'SelfWeight'
                ? t('beam.loads.selfWeightFull')
                : t('beam.loads.loadValueKnm')}
            </label>
            {selectedLoadType === 'SelfWeight' ? (
              <input type="text" className="form-control bg-light text-muted" value={autoSelfWeight.toFixed(4)} readOnly />
            ) : (
              <NumberInput
                value={loadForm.uniformIntensity}
                onCommit={(value) => updateForm({ uniformIntensity: value })}
              />
            )}
          </div>
        )}

        {selectedLoadType === 'PointLoad' && (
          <>
            <div className="col-6">
              <label className="form-label">{t('beam.loads.loadValueKn')}</label>
              <NumberInput
                value={loadForm.pointLoad}
                onCommit={(value) => updateForm({ pointLoad: value })}
              />
            </div>
            <div className="col-6">
              <label className="form-label">{t('beam.loads.position')}</label>
              <NumberInput
                value={loadForm.pointLoadPosition}
                onCommit={(value) => updateForm({ pointLoadPosition: value })}
              />
            </div>
          </>
        )}

        {(selectedLoadType === 'TriangularRight' || selectedLoadType === 'TriangularLeft') && (
          <>
            <div className="col-6">
              <label className="form-label">
                {selectedLoadType === 'TriangularRight' ? t('beam.loads.minValue') : t('beam.loads.maxValue')} (kN/m):
              </label>
              <NumberInput
                value={loadForm.startIntensity}
                onCommit={(value) => updateForm({ startIntensity: value })}
              />
            </div>
            <div className="col-6">
              <label className="form-label">
                {selectedLoadType === 'TriangularRight' ? t('beam.loads.maxValue') : t('beam.loads.minValue')} (kN/m):
              </label>
              <NumberInput
                value={loadForm.endIntensity}
                onCommit={(value) => updateForm({ endIntensity: value })}
              />
            </div>
            <div className="col-6">
              <label className="form-label">{t('beam.loads.startPosition')}</label>
              <NumberInput
                value={loadForm.startPosition}
                onCommit={(value) => updateForm({ startPosition: value })}
              />
            </div>
            <div className="col-6">
              <label className="form-label">{t('beam.loads.endPosition')}</label>
              <NumberInput
                value={loadForm.endPosition}
                onCommit={(value) => updateForm({ endPosition: value })}
              />
            </div>
          </>
        )}

        {selectedLoadType === 'AxialForce' && (
          <>
            <div className="col-4">
              <label className="form-label">{t('beam.loads.forceValue')}</label>
              <NumberInput
                value={loadForm.axialForceValue}
                onCommit={(value) => updateForm({ axialForceValue: value })}
              />
              <small className="text-muted">{t('beam.loads.posTensionNegCompression')}</small>
            </div>
            <div className="col-4">
              <label className="form-label">{t('beam.loads.eccentricity')}</label>
              <NumberInput
                value={loadForm.normalForceEccentricity}
                onCommit={(value) => updateForm({ normalForceEccentricity: value })}
              />
              <small className="text-muted">{t('beam.loads.eccentricityHint')}</small>
            </div>
            <div className="col-4">
              <label className="form-label">{t('beam.loads.applyAt')}</label>
              <select
                className="form-select"
                value={loadForm.axialForceEnd}
                onChange={handleAxialForceEndChange}
              >
                <option value="left">{t('beam.loads.leftEnd')}</option>
                <option value="right">{t('beam.loads.rightEnd')}</option>
              </select>
            </div>
          </>
        )}
      </div>

      <div className="d-flex gap-2 mt-2">
        <button className={`btn ${editingLoadId ? 'btn-success' : 'btn-primary'}`} onClick={addLoadToSpan} type="button">
          <i className={`bi ${editingLoadId ? 'bi-check-lg' : 'bi-plus'}`}></i> {editingLoadId ? t('beam.loads.saveChanges') : t('beam.loads.addLoad')}
        </button>
        {editingLoadId && onCancelEdit && (
          <button className="btn btn-outline-secondary" onClick={onCancelEdit} type="button">
            <i className="bi bi-x-lg"></i> {t('beam.loads.cancel')}
          </button>
        )}
      </div>
    </div>
  )
}

export default LoadInputForm
